import { X } from 'lucide-react';
import { formatCnpj } from '../../utils/masks';

// ZYN-311: combobox de vínculo de matriz/grupo econômico extraído do
// ContaFormModal — duas seções (sugestões por CNPJ raiz = filial legal,
// busca livre = franquia/grupo econômico). Estado do dropdown (parentQuery,
// parentSelected, parentCandidates, parentResults) permanece no hook
// useContaForm; este componente é só apresentação + interação local.
export default function ParentContaCombobox({
  form, set,
  parentSelected, setParentSelected,
  parentQuery, setParentQuery,
  parentOpen, setParentOpen,
  parentCandidates, parentResults,
  parentBoxRef,
  inputClass,
}) {
  return (
    <div className="col-span-2">
      <label className="block text-xs font-medium text-gray-600 mb-1">
        Conta matriz / Grupo econômico
        <span className="text-gray-400 ml-1">(filial legal com mesmo CNPJ raiz OU franquia/grupo — busca livre)</span>
      </label>
      <div className="relative" ref={parentBoxRef}>
        {/* Badge da seleção atual — quando há matriz vinculada */}
        {parentSelected && !parentOpen ? (
          <div className="flex items-center gap-2 w-full px-3 py-2 border border-gray-300 rounded-lg bg-white">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-[#002233] truncate">
                  {parentSelected.nome || parentSelected.razao_social}
                </span>
                {parentSelected.cnpj && form.cnpj &&
                  parentSelected.cnpj.replace(/\D/g, '').slice(0, 8) === form.cnpj.replace(/\D/g, '').slice(0, 8) ? (
                  <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full uppercase tracking-wider">
                    filial (mesmo CNPJ raiz)
                  </span>
                ) : (
                  <span className="text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full uppercase tracking-wider">
                    grupo econômico
                  </span>
                )}
              </div>
              <div className="text-xs text-gray-500 mt-0.5">
                {parentSelected.cnpj ? formatCnpj(parentSelected.cnpj) : '—'}
                {parentSelected.cidade && ` · ${parentSelected.cidade}${parentSelected.estado ? `/${parentSelected.estado}` : ''}`}
              </div>
            </div>
            <button type="button" onClick={() => setParentOpen(true)}
              className="text-xs text-[#00DBFF] hover:underline flex-shrink-0">
              Trocar
            </button>
            <button type="button"
              onClick={() => { set('parent_conta_id', ''); setParentSelected(null); }}
              title="Remover vínculo"
              className="text-gray-400 hover:text-red-500 flex-shrink-0">
              <X size={14} />
            </button>
          </div>
        ) : (
          <input
            type="text"
            value={parentQuery}
            onChange={e => { setParentQuery(e.target.value); setParentOpen(true); }}
            onFocus={() => setParentOpen(true)}
            placeholder="Buscar por nome, razão social ou CNPJ — ou deixar vazio se esta é a matriz"
            className={`${inputClass} bg-white`}
          />
        )}

        {/* Dropdown */}
        {parentOpen && (
          <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-y-auto">
            {/* Sugestões por CNPJ raiz (candidato a filial legal) */}
            {parentCandidates.length > 0 && (
              <>
                <div className="px-3 py-1.5 bg-emerald-50 border-b border-emerald-100">
                  <p className="text-[10px] text-emerald-700 font-medium uppercase tracking-wide">
                    Sugestões — mesma raiz CNPJ (filiais)
                  </p>
                </div>
                {parentCandidates.map(c => (
                  <button
                    key={`cand-${c.id}`}
                    type="button"
                    onMouseDown={e => e.preventDefault()}
                    onClick={() => {
                      set('parent_conta_id', c.id);
                      setParentSelected(c);
                      setParentOpen(false);
                      setParentQuery('');
                    }}
                    className="w-full text-left px-3 py-2 hover:bg-emerald-50 border-b border-gray-50 transition-colors"
                  >
                    <p className="text-sm font-medium text-gray-800 truncate">{c.nome}</p>
                    <p className="text-xs text-gray-500">
                      {c.cnpj ? formatCnpj(c.cnpj) : '—'}
                      {c.cidade && ` · ${c.cidade}${c.estado ? `/${c.estado}` : ''}`}
                    </p>
                  </button>
                ))}
              </>
            )}

            {/* Resultados da busca livre */}
            {parentQuery.trim().length >= 2 && (
              <>
                <div className="px-3 py-1.5 bg-purple-50 border-b border-purple-100">
                  <p className="text-[10px] text-purple-700 font-medium uppercase tracking-wide">
                    Busca — grupo econômico / franquia
                  </p>
                </div>
                {parentResults.length === 0 ? (
                  <div className="px-3 py-2.5 text-xs text-gray-400">
                    Nenhuma conta encontrada para "{parentQuery}"
                  </div>
                ) : (
                  parentResults
                    .filter(c => !parentCandidates.some(pc => pc.id === c.id))
                    .map(c => (
                      <button
                        key={`res-${c.id}`}
                        type="button"
                        onMouseDown={e => e.preventDefault()}
                        onClick={() => {
                          set('parent_conta_id', c.id);
                          setParentSelected(c);
                          setParentOpen(false);
                          setParentQuery('');
                        }}
                        className="w-full text-left px-3 py-2 hover:bg-purple-50 border-b border-gray-50 transition-colors"
                      >
                        <p className="text-sm font-medium text-gray-800 truncate">{c.nome}</p>
                        <p className="text-xs text-gray-500">
                          {c.cnpj ? formatCnpj(c.cnpj) : '—'}
                          {c.cidade && ` · ${c.cidade}${c.estado ? `/${c.estado}` : ''}`}
                        </p>
                      </button>
                    ))
                )}
              </>
            )}

            {parentCandidates.length === 0 && parentQuery.trim().length < 2 && (
              <div className="px-3 py-3 text-xs text-gray-400">
                Digite 2+ caracteres para buscar uma conta de grupo econômico (franquia, matriz de rede),
                ou preencha o CNPJ acima para ver sugestões de filiais.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
